import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectAuthData,
  selectCallbackRemindersEnabled,
  selectExtraRoutesAuthData,
} from '@/containers/Login/AuthSelectors'
import { scheduleCallbackModuleActions } from '@/containers/SubscriptionFormPageNew/Components/ScheduleCallback/ScheduleCallbackSagas'
import { modulesConfig } from '@/containers/SubscriptionFormPageNew/config'
import { selectScheduledCallbackToday } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import {
  closeTodayCallbacks,
  openTodayCallbacks,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import {
  formatCallbackTime,
  minutesUntilCallback,
} from '@/shared/components/CallbackReminderPopup/callbackReminderDates'
import useNow from '@/shared/hooks/useNow'
import { TodayCallback, TodayCallbackRowView, TodayCallbacksState } from './types'
import { TodayCallbacksDrawerOverrides } from './styled-components'

const LABEL_PREFIX = 'subscriptionTools:schedule_callback.today_callbacks'
const CLOSED_OUTCOMES = ['made', 'cancelled']

const customerName = (callback: TodayCallback): string =>
  `${callback.contact_first_name ?? ''} ${callback.contact_last_name ?? ''}`.trim()

const outcomeLabel = (t: TFunction, callback: TodayCallback): string =>
  CLOSED_OUTCOMES.includes(callback.outcome) ? t(`${LABEL_PREFIX}.outcome.${callback.outcome}`) : ''

const useTodayCallbacks = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { country: rawCountry }: { country?: string } = useSelector(selectAuthData) ?? {}
  const remindersEnabled: boolean = useSelector(selectCallbackRemindersEnabled)
  const { scheduleCallbackTodayUrl }: { scheduleCallbackTodayUrl?: string } = useSelector(selectExtraRoutesAuthData)
  const today: TodayCallbacksState = useSelector(selectScheduledCallbackToday)
  const country = String(rawCountry ?? '').toLowerCase()
  const isReminderCountry =
    (modulesConfig as Record<string, { menu?: { callbackReminders?: boolean } }>)[country]?.menu?.callbackReminders ??
    false
  const isAvailable = remindersEnabled && isReminderCountry && Boolean(scheduleCallbackTodayUrl)
  const items = today.items ?? []
  const isLoading = Boolean(today.isLoading)
  const hasError = !isLoading && Boolean(today.error)
  const isOpen = Boolean(today.isOpen)
  const now = useNow(isOpen)

  const load = () => dispatch({ type: scheduleCallbackModuleActions.LOAD_TODAY_CALLBACKS })

  const open = () => {
    dispatch(openTodayCallbacks())
    load()
  }

  const close = () => dispatch(closeTodayCallbacks())

  const rows: TodayCallbackRowView[] = items.map(callback => ({
    id: callback.id,
    time: formatCallbackTime(callback.scheduled_at),
    customer: customerName(callback),
    phone: callback.contact_phone ?? '',
    isLate: minutesUntilCallback(callback.scheduled_at, now) <= 0 && !CLOSED_OUTCOMES.includes(callback.outcome),
    lateLabel: t(`${LABEL_PREFIX}.late`),
    outcomeLabel: outcomeLabel(t, callback),
    hasContact: Boolean(callback.zoho_contact_id),
    openContactLabel: t(`${LABEL_PREFIX}.open_contact`),
    onOpenContact: () => {
      window.open(`/contact/${callback.zoho_contact_id}`, '_blank')
    },
  }))

  return {
    isAvailable,
    isOpen,
    isLoading,
    hasError,
    isEmpty: !isLoading && !hasError && rows.length === 0,
    rows,
    open,
    close,
    retry: load,
    buttonLabel: t(`${LABEL_PREFIX}.button`),
    title: t(`${LABEL_PREFIX}.title`),
    emptyLabel: t(`${LABEL_PREFIX}.empty`),
    errorLabel: t(`${LABEL_PREFIX}.error`),
    retryLabel: t(`${LABEL_PREFIX}.retry`),
    loadingLabel: t(`${LABEL_PREFIX}.loading`),
    drawerOverrides: TodayCallbacksDrawerOverrides,
  }
}

export default useTodayCallbacks
